/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    ControlType,
    FieldType,
    ISettingGroupDefinition,
    Levels,
    NumberFieldDefinition,
    NumberFormGroup,
    SettingGroupDefinition,
    StringResource,
    UIDefinitions
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelSpinData,
    IFieldDefinitionPanelSpinProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";

const logger: ILogger = getLogger();

export class DomainDataMapperSpin implements IDomainDataMapper {
    /**
     * Field type expected by this mapper
     */
    readonly RequiredFieldType = FieldType.Number;

    /**
     * Control type expected by this mapper
     */
    readonly RequiredControlType = ControlType.NumberField;

    /**
     * Setting group definition object
     */
    readonly definition: ISettingGroupDefinition;

    constructor(definition: ISettingGroupDefinition) {
        this.definition = definition;
    }

    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const result: IFieldDefinitionPanelSpinProps = {
            common: {
                panelTitle: "Add Spin Button",
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: ControlType.NumberField,
                settingName: "",
                title: "",
                tooltip: "",
                helper: "",
                helpTextValid: true,
                defaultValue: 0,
                minValue: 0,
                maxValue: 10,
                step: 1,
                accessLevels: Levels.Classic,
                auditChanges: false,
                visibleRule: "",
                enableRule: "",
                suffix: ""
            }
        };

        return result;
    };

    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const [nfg, nfd] = this.findControlAndFieldDefinition(sg, ctrlGuid);
        const sr = sg.UI.StringResources;

        const result: IFieldDefinitionPanelSpinProps = {
            common: {
                panelTitle: "Edit Spin Button",
                originalSettingName: nfd.SettingName,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid: nfg.CtrlGuid,
                definitionType: ControlType.NumberField,
                settingName: nfd.SettingName,
                title:
                    sr.get(StringResource.CreateMapKey(nfg.LabelTextStringId, "en-US"))?.Text ?? "",
                tooltip:
                    sr.get(StringResource.CreateMapKey(nfg.TooltipStringId, "en-US"))?.Text ?? "",
                helper:
                    sr.get(StringResource.CreateMapKey(nfg.HelperTextStringId, "en-US"))?.Text ??
                    "",
                helpTextValid: true,
                defaultValue: nfd.DefaultValue,
                minValue: nfd.MinValue,
                maxValue: nfd.MaxValue,
                step: nfg.Step ?? 1,
                accessLevels: nfd.AccessLevel,
                auditChanges: nfd.AuditChanges,
                visibleRule: nfg.VisibleRuleName,
                enableRule: nfg.EnableRuleName,
                suffix: sr.get(StringResource.CreateMapKey(nfg.SuffixStringId, "en-US"))?.Text ?? ""
            }
        };

        return result;
    };

    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== ControlType.NumberField) {
            logger.logError("Invalid control type", { type: ControlType[data.definitionType] });
            throw new Error("Invalid definition type.");
        }

        const sd = data as IFieldDefinitionPanelSpinData;
        const sg = new SettingGroupDefinition(this.definition);

        if (data.ctrlGuid === undefined) {
            this.addNewNumberField(sd, sg);
        } else {
            this.updateExistingNumberField(sd, sg);
        }
        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new number field to the setting definition
     * @param sd Panel spin panel data
     * @param sg Setting group definition
     */
    private addNewNumberField(sd: IFieldDefinitionPanelSpinData, sg: SettingGroupDefinition): void {
        const nfd = new NumberFieldDefinition(sd.settingName, sd.defaultValue);
        nfd.AccessLevel = sd.accessLevels;
        nfd.AuditChanges = sd.auditChanges;
        nfd.MaxValue = sd.maxValue;
        nfd.MinValue = sd.minValue;
        sg.AddFieldDefinition(nfd);

        const nfg = sg.UI.AddNumberFormGroup(sd.settingName);
        nfg.Step = sd.step;
        nfg.EnableRuleName = sd.enableRule;
        nfg.VisibleRuleName = sd.visibleRule;

        this.updateStringResources(sg.UI, nfg, sd.title, sd.tooltip, sd.helper, sd.suffix);
    }

    /**
     *
     * @param sd Panel spin data
     * @param sg Setting group definition
     */
    private updateExistingNumberField(
        sd: IFieldDefinitionPanelSpinData,
        sg: SettingGroupDefinition
    ): void {
        if (sd.ctrlGuid === undefined) {
            logger.logError("Control guid is undefined.");
            throw new Error("Control guid is undefined.");
        }

        const [nfg, nfd] = this.findControlAndFieldDefinition(sg, sd.ctrlGuid);
        nfd.DefaultValue = sd.defaultValue;
        nfd.AccessLevel = sd.accessLevels;
        nfd.AuditChanges = sd.auditChanges;
        nfd.MaxValue = sd.maxValue;
        nfd.MinValue = sd.minValue;
        nfg.Step = sd.step;
        nfg.EnableRuleName = sd.enableRule;
        nfg.VisibleRuleName = sd.visibleRule;

        this.updateStringResources(sg.UI, nfg, sd.title, sd.tooltip, sd.helper, sd.suffix);
    }

    /**
     * Updates the string resources in the definition.
     * @param sg Setting group definition class
     * @param tfg Text form group
     * @param label Label text
     * @param tooltip Tooltip text
     * @param suffix Suffix text
     */
    private updateStringResources(
        ui: UIDefinitions,
        nfg: NumberFormGroup,
        label: string,
        tooltip: string,
        helper: string,
        suffix: string
    ) {
        if (!ui.UpdateStringResourceText(nfg.LabelTextStringId, label)) {
            logger.logError("Error finding label string resource", {
                stringId: nfg.LabelTextStringId
            });
            throw new Error("Error getting label string resource");
        }

        if (!ui.UpdateStringResourceText(nfg.TooltipStringId, tooltip)) {
            logger.logError("Error finding tooltip string resource", {
                stringId: nfg.TooltipStringId
            });
            throw new Error("Error getting tooltip string resource");
        }

        if (!ui.UpdateStringResourceText(nfg.HelperTextStringId, helper)) {
            logger.logError("Error finding helper string resource", {
                stringId: nfg.HelperTextStringId
            });
            throw new Error("Error getting helper string resource");
        }

        if (!ui.UpdateStringResourceText(nfg.SuffixStringId, suffix)) {
            logger.logError("Error finding suffix string resource", {
                stringId: nfg.SuffixStringId
            });
            throw new Error("Error getting suffix string resource");
        }
    }

    private findControlAndFieldDefinition = (
        sg: SettingGroupDefinition,
        ctrlGuid: string
    ): [NumberFormGroup, NumberFieldDefinition] => {
        const uiCtrl = sg.UI.Controls.find(i => i.CtrlGuid === ctrlGuid);

        if (uiCtrl === undefined) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        if (uiCtrl.ControlDiscriminator !== this.RequiredControlType) {
            logger.logError("Control type is not number", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error("Invalid control type.  Expecting TextField control.");
        }

        const nfg = uiCtrl as NumberFormGroup;
        const fd = sg.Fields.get(nfg.SettingName);
        if (fd === undefined || fd.FieldDiscriminator !== this.RequiredFieldType) {
            logger.logError("Field type is not number", {
                type: fd?.FieldDiscriminator.toString()
            });
            throw new Error("Invalid field type.");
        }

        return [nfg, fd as NumberFieldDefinition];
    };
}
